import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubmissionTrackingPanel = ({ submissions }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-amber-500 bg-amber-500/10',
      approved: 'text-emerald-500 bg-emerald-500/10',
      rejected: 'text-red-500 bg-red-500/10'
    };
    return colors?.[status] || 'text-muted-foreground bg-muted';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      approved: 'CheckCircle',
      rejected: 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  const filteredSubmissions = filter === 'all' 
    ? submissions 
    : submissions?.filter(sub => sub?.status === filter);

  const statusCounts = {
    all: submissions?.length,
    pending: submissions?.filter(s => s?.status === 'pending')?.length,
    approved: submissions?.filter(s => s?.status === 'approved')?.length,
    rejected: submissions?.filter(s => s?.status === 'rejected')?.length
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 border border-border">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Submission Tracking</h2>
          <p className="text-sm text-muted-foreground">Monitor your reward submissions</p>
        </div>
        <Button
          variant="default"
          onClick={() => navigate('/transaction-submission-screen')}
          iconName="Plus"
          iconPosition="left"
          size="sm"
        >
          New Submission
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'approved', 'rejected']?.map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 ${
              filter === status
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {status?.charAt(0)?.toUpperCase() + status?.slice(1)} ({statusCounts?.[status]})
          </button>
        ))}
      </div>
      <div className="overflow-x-auto -mx-6 md:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full divide-y divide-border">
            <thead>
              <tr className="bg-muted/50">
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Brand
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Reward
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredSubmissions?.map((submission) => (
                <tr key={submission?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Store" size={16} color="var(--color-primary)" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{submission?.brandName}</span>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-foreground font-medium">${submission?.purchaseAmount?.toFixed(2)}</span>
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-primary">${submission?.rewardAmount?.toFixed(2)}</span>
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(submission?.status)}`}>
                      <Icon name={getStatusIcon(submission?.status)} size={14} />
                      {submission?.status?.charAt(0)?.toUpperCase() + submission?.status?.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-muted-foreground">{submission?.submissionDate}</span>
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => navigate('/user-dashboard')}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredSubmissions?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-muted-foreground mb-4">No {filter !== 'all' ? filter : ''} submissions found</p>
          <Button variant="outline" onClick={() => setFilter('all')}>
            Show All
          </Button>
        </div>
      )}
    </div>
  );
};

export default SubmissionTrackingPanel;